import { css } from '@styled-system/css';
import { stack } from '@styled-system/patterns';
import type { FC } from 'react';

import { Button } from '@/shared/components/ui/Button';
import { Message } from '@/shared/components/ui/Message';
import { SearchBar } from '@/shared/components/ui/SearchBar';
import { useIsMobile } from '@/shared/hooks/useIsMobile';

interface AnalysisMenuHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  showCreateButton?: boolean;
  onCreateNew?: () => void;
  maxAnalysesReached?: boolean;
  noSubscriptionActive?: boolean;
  navigateToSubscription?: () => void;
  isLoading?: boolean;
  handleExport?: () => Promise<void>;
}

export const AnalysisMenuHeader: FC<AnalysisMenuHeaderProps> = ({
  searchQuery,
  onSearchChange,
  showCreateButton = false,
  onCreateNew,
  maxAnalysesReached = false,
  noSubscriptionActive = false,
  navigateToSubscription,
  isLoading = false,
  handleExport,
}) => {
  const isMobile = useIsMobile();

  const headerStyles = css({
    position: 'relative',
    px: '0.5625rem', // 9px
    pt: '0.875rem', // 14px
    pb: 'lg', // 35px
  });

  const maxAnalysesReachedMessageStyles = css({
    mt: 'sm',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  });

  const createButtonStyles = css({
    bg: 'accent.blue!important',
    _hover: { bg: 'accent.blue!important', opacity: 0.9 },
  });

  const noSubscriptionMessageStyles = css({
    position: 'absolute',
    top: 0,
    mt: 'sm',
    fontSize: '2xs',
    bg: 'base.segBg',
  });

  return (
    <div className={headerStyles}>
      {/* Search Bar */}
      <SearchBar
        placeholder="Rechercher une analyse"
        value={searchQuery}
        onChange={onSearchChange}
        className={css({ mb: 'sm' })} // 10px margin bottom
      />

      {showCreateButton && (
        <div className={stack({ direction: 'row', align: 'center', justify: 'space-between', gap: 'sm' })}>
          {/* Add Button */}
          <Button handleClick={onCreateNew} icon="plus" disabled={isLoading} className={createButtonStyles}>
            Ajouter
          </Button>

          {/* Export Button */}
          <Button handleClick={handleExport} icon="download">
            Exporter
          </Button>
        </div>
      )}

      {maxAnalysesReached && (
        <div onClick={navigateToSubscription} className={maxAnalysesReachedMessageStyles}>
          <Message type="info" bordered textColor="font.primary">
            Maximum d’analyses atteint, veuillez mettre fin à une analyse en cours ou passer à un abonnement supérieur
          </Message>
        </div>
      )}

      {isMobile && !maxAnalysesReached && noSubscriptionActive && (
        <div onClick={navigateToSubscription} className={noSubscriptionMessageStyles}>
          <Message type="info" bordered textColor="font.primary">
            Vous avez accès à un aperçu. Abonnez-vous pour avoir accès à la totalité des fonctionnalités.{' '}
          </Message>
        </div>
      )}
    </div>
  );
};
